// File: src/components/JobRequirements/MappedStudents/MappedStudentsTable.js

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../Admissionform/ToastManager';
import MappedStudentsActions from './MappedStudentsActions';
import { formatDistanceToNow } from "date-fns";

const FEEDBACK_REQUIRED_STATUSES = [
    'Cancelled',
    'Not Selected',
    'Rejected By Student',
    'Rejected the Offer',
    'Not Attended'
];

const STATUS_OPTIONS = [
    'Assigned',
    'Acknowledged',
    'Shortlisted',
    'Resume Not Shortlisted',
    'Interview Scheduled',
    'Not Attended',
    'Cancelled',
    'Selected',
    'Not Selected',
    'Rejected By Student',
];

const getDegreeInfo = (student) => {
    const degrees = [];
    if (student.diplomaDegree && student.diplomaDegree.trim() !== '') {
        degrees.push(`${student.diplomaDegree} - ${student.diplomaDepartment || ''}`);
    }
    if (student.ugDegree && student.ugDegree.trim() !== '') {
        degrees.push(`${student.ugDegree} - ${student.ugDepartment || ''}`);
    }
    if (student.pgDegree && student.pgDegree.trim() !== '') {
        degrees.push(`${student.pgDegree} - ${student.pgDepartment || ''}`);
    }
    return degrees.length > 0 ? degrees.join(', ') : '';
};

const getPassedOutYears = (student) => {
    const years = [];
    if (student.diplomaPassout) years.push(`Diploma: ${student.diplomaPassout}`);
    if (student.ugPassout) years.push(`UG: ${student.ugPassout}`);
    if (student.pgPassout) years.push(`PG: ${student.pgPassout}`);
    return years.length > 0 ? years.join(', ') : '';
};

const truncateText = (text, maxLength = 20) => {
    if (!text || text === '') return text;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatLastLogin = (date) => {
    if (!date) return "Never logged in";
    return formatDistanceToNow(new Date(date), { addSuffix: true });
};

const SkillsCell = React.memo(({ student }) => {
    const matchedSkills = student.matchedSkills || [];
    const displaySkills = matchedSkills.slice(0, 2);
    const remainingCount = matchedSkills.length - 2;
    const allSkillsText = matchedSkills.join(', ');

    return (
        <div className="skills-cell" title={allSkillsText}>
            {matchedSkills.length > 0 ? (
                <div className="matched-skills-container">
                    {displaySkills.map((skill, index) => (
                        <span key={index} className="matched-skill-tag">
                            {truncateText(skill, 10)}
                        </span>
                    ))}
                    {remainingCount > 0 && (
                        <span className="more-skills-badge">+{remainingCount}</span>
                    )}
                </div>
            ) : (
                <span className="no-skills">—</span>
            )}
        </div>
    );
});

const StatusBadge = React.memo(({ status }) => (
    <span className={`status-badge status-${status?.toLowerCase().replace(/ /g, '-')}`}>
        {status}
    </span>
));

const StatusEditor = React.memo(({
    student,
    editingStatus,
    statusData,
    updating,
    onEditStart,
    onStatusChange,
    onFeedbackChange,
    onSave,
    onCancel
}) => {
    const mappingId = student.mappingId || student.id;
    const isEditing = editingStatus === mappingId;
    const [hasInteracted, setHasInteracted] = useState(false);

    useEffect(() => {
        if (!isEditing) setHasInteracted(false);
    }, [isEditing]);

    const requiresFeedback = FEEDBACK_REQUIRED_STATUSES.includes(statusData.currentStatus);

    const handleSelectChange = (e) => {
        setHasInteracted(true);
        onStatusChange(e);
    };

    if (!isEditing) {
        return (
            <div className="status-display" onClick={() => onEditStart(student)}>
                <StatusBadge status={student.currentStatus} />
                {student.feedback && (
                    <div className="feedback-text">{student.feedback}</div>
                )}
                <button className="edit-status-btn" title="Edit Status">✎</button>
            </div>
        );
    }

    return (
        <div className="status-editor">
            <select
                value={statusData.currentStatus}
                onChange={handleSelectChange}
                className="status-select"
                disabled={updating}
            >
                <option value="">Select Status</option>
                {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                ))}
            </select>

            {hasInteracted && statusData.currentStatus && (
                <textarea
                    key={mappingId}
                    value={statusData.feedback}
                    onChange={onFeedbackChange}
                    placeholder={requiresFeedback ? 'Reason is required...' : 'Add feedback (optional)...'}
                    className={`feedback-input ${requiresFeedback ? 'feedback-required' : ''}`}
                    rows="2"
                    disabled={updating}
                    autoFocus
                    onFocus={(e) => {
                        const len = e.target.value.length;
                        e.target.setSelectionRange(len, len);
                    }}
                />
            )}

            <div className="status-actions">
                <button onClick={() => onSave(mappingId)} className="save-status-btn" disabled={updating}>
                    {updating ? 'Saving...' : 'Save'}
                </button>
                <button onClick={onCancel} className="cancel-status-btn" disabled={updating}>
                    Cancel
                </button>
            </div>
        </div>
    );
});

const StudentRow = React.memo(({
    student,
    isChecked,
    onSelect,
    onStudentClick,
    editingStatus,
    statusData,
    updating,
    onEditStart,
    onStatusChange,
    onFeedbackChange,
    onSave,
    onCancel
}) => {
    const mappingId = student.mappingId || student.id;
    const degreeInfo = getDegreeInfo(student);
    const yearInfo = getPassedOutYears(student);
    const dbId = student.id;

    return (
        <tr
            className={
                isChecked
                    ? `row-selected ${student.placementInterest === "Critical"
                        ? "critical-row"
                        : student.ProgressStatus === "Course Completed"
                            ? "completed-row"
                            : ""
                    }`
                    : (
                        student.placementInterest === "Critical"
                            ? "critical-row"
                            : student.ProgressStatus === "Course Completed"
                                ? "completed-row"
                                : ""
                    )
            }
            title={
                student.placementInterest === "Critical"
                    ? student.staffRemarks || "No remarks"
                    : ""
            }
        >
            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onSelect(mappingId)}
                />
            </td>

            <td className="stack-cell">
                <span className="student-name-link" onClick={() => onStudentClick(dbId)}>
                    {student.studentName || ""}
                </span>
                <div style={{ fontSize: "12px", color: "#888" }}>
                    Last login {formatLastLogin(student.lastLogin)}
                </div>
                <div className="sub-text">{student.branch || ""}</div>
                <div className="sub-text" style={{ color: "#666" }}>{student.courseName || ""}</div>
            </td>

            <td className="stack-cell" title={`${degreeInfo} | ${yearInfo}`}>
                <div className="primary-text">{degreeInfo}</div>
                <div className="sub-text">{yearInfo}</div>
            </td>

            <td className="truncate-cell" title={student.desiredlocation || ""}>
                {student.desiredlocation || ""}
            </td>

            <td><SkillsCell student={student} /></td>

            <td className="match-percent">{student.matchScore || 0}%</td>

            <td>
                <div style={{ lineHeight: "1.4" }}>
                    <div style={{ fontWeight: "500" }}>{student.mappedByName || "N/A"}</div>
                    <div style={{ color: "#555", fontSize: "13px" }}>{student.mappedByContact || ""}</div>
                    <div style={{ color: "#888", fontSize: "12px" }}>
                        {student.createdAt
                            ? new Date(student.createdAt).toLocaleString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit", hour12: true
                            })
                            : ""}
                    </div>
                </div>
            </td>

            <td style={{ width: "150px" }}>
                <div style={{ fontWeight: "500" }}>{student.acknowledgementStatus || "Pending"}</div>
                {student.acknowledgementDate && (
                    <>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                            {new Date(student.acknowledgementDate).toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric"
                            })}
                        </div>
                        <div style={{ fontSize: "12px", color: "#888" }}>
                            {new Date(student.acknowledgementDate).toLocaleTimeString("en-IN", {
                                hour: "2-digit", minute: "2-digit", hour12: true
                            })}
                        </div>
                    </>
                )}
            </td>

            <td className="status-cell">
                <StatusEditor
                    student={student}
                    editingStatus={editingStatus}
                    statusData={statusData}
                    updating={updating}
                    onEditStart={onEditStart}
                    onStatusChange={onStatusChange}
                    onFeedbackChange={onFeedbackChange}
                    onSave={onSave}
                    onCancel={onCancel}
                />
            </td>
        </tr>
    );
});

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const MappedStudentsTable = ({
    students,
    loading,
    hasFetchedRef,
    isAnyFilterApplied,
    onClearFilters,
    onStatusUpdate,
    jobReqId,
    currentStatus,
    acknowledgementStatus,
    branch,
    studentName,
    placementInterest,
    fromDate,
    toDate,
    selectedIds,
    onSelectedIdsChange,
}) => {
    const navigate = useNavigate();
    const [editingStatus, setEditingStatus] = useState(null);
    const [statusData, setStatusData] = useState({ currentStatus: '', feedback: '' });
    const [updating, setUpdating] = useState(false);
    const { showSuccess, showError, showWarning } = useToast();
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [bulkFeedback, setBulkFeedback] = useState('');

    const tableWrapperRef = useRef(null);
    const scrollPosRef = useRef(0);

    useEffect(() => {
        if (tableWrapperRef.current && scrollPosRef.current > 0) {
            tableWrapperRef.current.scrollTop = scrollPosRef.current;
        }
    }, [selectedIds]);

    const branchBreakdown = useMemo(() => {
        const counts = {};
        students.forEach(s => {
            if (s.branch && s.branch.trim()) {
                const b = s.branch.trim();
                counts[b] = (counts[b] || 0) + 1;
            }
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    }, [students]);

    const handleSelectAll = useCallback((e) => {
        if (tableWrapperRef.current) scrollPosRef.current = tableWrapperRef.current.scrollTop;
        if (e.target.checked) {
            onSelectedIdsChange(new Set(students.map(s => s.mappingId || s.id)));
        } else {
            onSelectedIdsChange(new Set());
        }
    }, [students, onSelectedIdsChange]);

    const handleSelectRow = useCallback((id) => {
        if (tableWrapperRef.current) scrollPosRef.current = tableWrapperRef.current.scrollTop;
        onSelectedIdsChange(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, [onSelectedIdsChange]);

    const handleBulkStatusUpdate = async () => {
        if (!bulkStatus) { showWarning('Please select a status to apply'); return; }
        if (selectedIds.size === 0) { showWarning('Please select at least one student'); return; }
        if (FEEDBACK_REQUIRED_STATUSES.includes(bulkStatus) && !bulkFeedback.trim()) {
            showWarning('Please provide a reason for this status'); return;
        }

        setBulkUpdating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/mapped-students/bulk-status`,
                {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        mappingIds: Array.from(selectedIds),
                        currentStatus: bulkStatus,
                        feedback: bulkFeedback?.trim() || null
                    })
                }
            );

            const result = await response.json();
            if (result.success) {
                showSuccess(`Status updated for ${selectedIds.size} student(s)`);
                onSelectedIdsChange(new Set()); // intentional clear after bulk action
                setBulkStatus('');
                setBulkFeedback('');
                if (onStatusUpdate) onStatusUpdate();
            } else {
                showError(result.message || 'Bulk update failed');
            }
        } catch (error) {
            console.error('Bulk status update error:', error);
            showError('Error during bulk update. Please try again.');
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleStatusEdit = useCallback((student) => {
        const mappingId = student.mappingId || student.id;
        setEditingStatus(mappingId);
        setStatusData({ currentStatus: student.currentStatus || '', feedback: student.feedback || '' });
    }, []);

    const handleStatusChange = useCallback((e) => {
        const newStatus = e.target.value;
        setStatusData(prev => ({
            ...prev,
            currentStatus: newStatus,
            feedback: FEEDBACK_REQUIRED_STATUSES.includes(newStatus) ? prev.feedback : ''
        }));
    }, []);

    const handleFeedbackChange = useCallback((e) => {
        setStatusData(prev => ({ ...prev, feedback: e.target.value }));
    }, []);

    const updateStatus = useCallback(async (mappingId) => {
        const { currentStatus, feedback } = statusData;
        if (FEEDBACK_REQUIRED_STATUSES.includes(currentStatus) && !feedback.trim()) {
            showWarning('Please provide a reason for this status');
            return;
        }

        setUpdating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/mapped-students/status/${mappingId}`,
                {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStatus, feedback: feedback || null })
                }
            );

            const result = await response.json();
            if (result.success) {
                setEditingStatus(null);
                setStatusData({ currentStatus: '', feedback: '' });
                if (onStatusUpdate) onStatusUpdate();
                showSuccess('Status updated successfully');
            } else {
                showError(result.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showError('Error updating status. Please try again.');
        } finally {
            setUpdating(false);
        }
    }, [statusData, onStatusUpdate, showSuccess, showError, showWarning]);

    const handleCancelEdit = useCallback(() => {
        setEditingStatus(null);
        setStatusData({ currentStatus: '', feedback: '' });
    }, []);

    const handleStudentClick = useCallback((studentId) => {
        const currentPath = window.location.pathname;
        sessionStorage.setItem('lastJobReqPath', currentPath);
        sessionStorage.setItem('lastActiveTab', 'mappedStudents');
        navigate(`/edits-student/${studentId}`);
    }, [navigate]);

    return (
        <div className="mapped-students-table-section">
            <div className="section-header">
                <div className="header-content">
                    <div className="header-left">
                        <h3 className="section-title">Mapped Students</h3>
                        <span className="student-count-badge">
                            {loading ? '...' : students.length} {students.length === 1 ? 'Student' : 'Students'}
                        </span>
                        {!loading && branchBreakdown.length > 0 && (
                            <div className="branch-breakdown">
                                {branchBreakdown.map(([b, count]) => (
                                    <span key={b} className="branch-chip">{b} – {count}</span>
                                ))}
                            </div>
                        )}
                    </div>

                    <MappedStudentsActions
                        jobReqId={jobReqId}
                        currentStatus={currentStatus}
                        acknowledgementStatus={acknowledgementStatus}
                        studentCount={students.length}
                        branch={branch}
                        studentName={studentName}
                        placementInterest={placementInterest}
                        fromDate={fromDate}
                        toDate={toDate}
                    />
                </div>
            </div>

            {loading && hasFetchedRef.current ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading students...</p>
                </div>
            ) : students.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <p className="empty-message">No mapped students found</p>
                    {isAnyFilterApplied && (
                        <button onClick={onClearFilters} className="clear-filters-btn">
                            Clear All Filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="table-wrapper" ref={tableWrapperRef}>

                    {selectedIds.size > 0 && (
                        <div className="bulk-action-toolbar">
                            <span className="bulk-selected-count">
                                {selectedIds.size} student{selectedIds.size > 1 ? 's' : ''} selected
                            </span>
                            <select
                                value={bulkStatus}
                                onChange={e => { setBulkStatus(e.target.value); setBulkFeedback(''); }}
                                className="status-select"
                                disabled={bulkUpdating}
                            >
                                <option value="">Select Status</option>
                                {STATUS_OPTIONS.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>

                            <textarea
                                value={bulkFeedback}
                                onChange={e => setBulkFeedback(e.target.value)}
                                placeholder={
                                    !bulkStatus
                                        ? 'Select status first...'
                                        : FEEDBACK_REQUIRED_STATUSES.includes(bulkStatus)
                                            ? 'Reason is required...'
                                            : 'Add feedback (optional)...'
                                }
                                className={`feedback-input bulk-feedback-input ${bulkStatus && FEEDBACK_REQUIRED_STATUSES.includes(bulkStatus) ? 'feedback-required' : ''}`}
                                rows="1"
                                disabled={!bulkStatus || bulkUpdating}
                            />

                            <button
                                onClick={handleBulkStatusUpdate}
                                className="save-status-btn"
                                disabled={bulkUpdating || !bulkStatus}
                            >
                                {bulkUpdating ? 'Updating...' : 'Apply to Selected'}
                            </button>
                            <button
                                onClick={() => {
                                    onSelectedIdsChange(new Set());
                                    setBulkFeedback('');
                                    setBulkStatus('');
                                }}
                                className="cancel-status-btn"
                                disabled={bulkUpdating}
                            >
                                Deselect All
                            </button>
                        </div>
                    )}

                    <table className="mapped-students-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={students.length > 0 && selectedIds.size === students.length}
                                        ref={el => {
                                            if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < students.length;
                                        }}
                                    />
                                </th>
                                <th>Student</th>
                                <th title="Preferred Job Location">Preferred</th>
                                <th>Skills</th>
                                <th>Match</th>
                                <th>Mapped By</th>
                                <th title="Acknowledgement" style={{ width: "150px" }}>Acknowledge</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {students.map((student) => {
                                const studentKey = student.mappingId || student.studentId || student.id;
                                const mappingId = student.mappingId || student.id;
                                return (
                                    <StudentRow
                                        key={studentKey}
                                        student={student}
                                        isChecked={selectedIds.has(mappingId)}
                                        onSelect={handleSelectRow}
                                        onStudentClick={handleStudentClick}
                                        editingStatus={editingStatus}
                                        statusData={statusData}
                                        updating={updating}
                                        onEditStart={handleStatusEdit}
                                        onStatusChange={handleStatusChange}
                                        onFeedbackChange={handleFeedbackChange}
                                        onSave={updateStatus}
                                        onCancel={handleCancelEdit}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MappedStudentsTable;